/**
 * /fx-rates — FX rate management.
 *
 * No dedicated /api/fx-rates or /api/currencies endpoint exists in the
 * backend (checked app/routers/ and app/main.py). FX revaluation is
 * triggered per-period via POST /api/periods/{id}/fx-revalue from the
 * Fiscal Periods page.
 *
 * This page explains the FX workflow and shows the FxRate model fields
 * (from_code, to_code, rate_date, rate) so users understand what drives
 * the revaluation. When a dedicated CRUD endpoint is added, replace the
 * informational panel with the rates table.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Alert,
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

export const Route = createFileRoute("/_auth/fx-rates")({
  component: FxRatesPage,
});

// ── Static explanation of the FX model fields ────────────────────────────────
const MODEL_FIELDS: Array<{ field: string; type: string; description: string }> = [
  {
    field: "from_code",
    type: "VARCHAR(3)",
    description: "ISO 4217 source currency code, e.g. EUR, GBP, CAD",
  },
  {
    field: "to_code",
    type: "VARCHAR(3)",
    description: "ISO 4217 target (base) currency code — typically USD",
  },
  {
    field: "rate_date",
    type: "DATE",
    description:
      "Effective date of the rate. The period-end FX reval uses the latest rate on or before the period end date.",
  },
  {
    field: "rate",
    type: "NUMERIC(18,8)",
    description:
      "Units of to_code per 1 unit of from_code. E.g. if from=EUR, to=USD, rate=1.08 means 1 EUR = 1.08 USD.",
  },
];

// ── How-to steps ─────────────────────────────────────────────────────────────
const STEPS = [
  {
    step: "1",
    title: "Load rates into fx_rates",
    body:
      "Insert rows into the fx_rates table (from_code, to_code, rate_date, rate). " +
      "You can seed these via a migration, the Django-style admin, or a future " +
      "import endpoint. The unique constraint is (from_code, to_code, rate_date).",
  },
  {
    step: "2",
    title: "Open the Periods page",
    body:
      'Navigate to Fiscal Periods. Any CLOSED period shows an "FX rev" button ' +
      "next to the Lock and Reopen actions.",
  },
  {
    step: "3",
    title: "Run FX revaluation",
    body:
      'Click "FX rev" on the target period. This posts a single FX_REVAL journal ' +
      "that revalues all non-base-currency monetary balances at the period-end rate. " +
      "The unrealized gain or loss hits the FX_GAIN / FX_LOSS system accounts. " +
      "Re-running is safe — only the delta is posted.",
  },
];

function FxRatesPage() {
  return (
    <Box sx={{ p: 3, maxWidth: 860 }}>
      {/* Page header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
        <CurrencyExchangeIcon color="primary" />
        <Typography variant="h5" fontWeight={600}>
          FX Rates
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
          Multi-currency revaluation · fx_rates table
        </Typography>
      </Box>

      {/* No dedicated endpoint notice */}
      <Alert
        severity="info"
        icon={<InfoOutlinedIcon />}
        sx={{ mb: 3 }}
        action={
          <Link to="/periods">
            <Button
              size="small"
              color="inherit"
              endIcon={<OpenInNewIcon fontSize="small" />}
              sx={{ textTransform: "none", whiteSpace: "nowrap" }}
            >
              Go to Periods
            </Button>
          </Link>
        }
      >
        <strong>No dedicated FX rates UI yet.</strong> FX revaluation runs from the
        Fiscal Periods page (FX rev button on any closed period). Rates are managed
        directly in the <code>fx_rates</code> database table until a CRUD endpoint
        is added.
      </Alert>

      {/* How it works */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
          How FX Revaluation Works
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {STEPS.map((s) => (
            <Box key={s.step} sx={{ display: "flex", gap: 2 }}>
              <Box
                sx={{
                  minWidth: 28,
                  height: 28,
                  borderRadius: "50%",
                  bgcolor: "primary.main",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                  fontSize: 13,
                  flexShrink: 0,
                  mt: 0.25,
                }}
              >
                {s.step}
              </Box>
              <Box>
                <Typography variant="subtitle2" fontWeight={600}>
                  {s.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {s.body}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Paper>

      {/* fx_rates model fields */}
      <Paper variant="outlined">
        <Box sx={{ px: 3, pt: 2.5, pb: 1 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            fx_rates Table Schema
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Unique constraint: (from_code, to_code, rate_date)
          </Typography>
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow
              sx={{
                "& th": {
                  fontWeight: 700,
                  bgcolor: "action.hover",
                  fontSize: 11,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                },
              }}
            >
              <TableCell>Column</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Description</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {MODEL_FIELDS.map((f) => (
              <TableRow key={f.field} hover>
                <TableCell sx={{ fontFamily: "monospace", fontWeight: 600, fontSize: 13 }}>
                  {f.field}
                </TableCell>
                <TableCell sx={{ fontFamily: "monospace", color: "text.secondary", fontSize: 12 }}>
                  {f.type}
                </TableCell>
                <TableCell sx={{ fontSize: 13 }}>{f.description}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {/* Example rates */}
      <Paper variant="outlined" sx={{ mt: 3, p: 3 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
          Example Rate Rows
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow
              sx={{
                "& th": {
                  fontWeight: 700,
                  bgcolor: "action.hover",
                  fontSize: 11,
                  textTransform: "uppercase",
                },
              }}
            >
              <TableCell>from_code</TableCell>
              <TableCell>to_code</TableCell>
              <TableCell>rate_date</TableCell>
              <TableCell align="right">rate</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {[
              { from: "EUR", to: "USD", date: "2025-03-31", rate: "1.08250000" },
              { from: "GBP", to: "USD", date: "2025-03-31", rate: "1.26400000" },
              { from: "CAD", to: "USD", date: "2025-03-31", rate: "0.73810000" },
              { from: "MXN", to: "USD", date: "2025-03-31", rate: "0.05180000" },
            ].map((r) => (
              <TableRow key={`${r.from}-${r.to}-${r.date}`}>
                <TableCell sx={{ fontFamily: "monospace", fontWeight: 600 }}>{r.from}</TableCell>
                <TableCell sx={{ fontFamily: "monospace" }}>{r.to}</TableCell>
                <TableCell sx={{ fontFamily: "monospace", color: "text.secondary" }}>
                  {r.date}
                </TableCell>
                <TableCell align="right" sx={{ fontFamily: "monospace" }}>
                  {r.rate}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
          These are illustrative examples only. Actual rates come from your fx_rates table.
        </Typography>
      </Paper>
    </Box>
  );
}
